import React, { useState, useEffect, useCallback } from 'react';
import { Check, X as XIcon, RefreshCw, Search, MessageSquare, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/utils/api';

const TABS = [
  { key: 'leaves', label: 'Leaves', countKey: 'leaves' },
  { key: 'rosters', label: 'Rosters', countKey: 'rosters' },
  { key: 'missed-punch', label: 'Missed Punch', countKey: 'missed_punch' },
  { key: 'transfers', label: 'Transfers', countKey: 'transfers' },
  { key: 'comp-off', label: 'Comp Off', countKey: 'comp_off' },
  { key: 'outdoor-duty', label: 'Outdoor Duty', countKey: 'outdoor_duty' },
  { key: 'ot', label: 'OT', countKey: 'ot' },
  { key: 'odw', label: 'ODW', countKey: 'odw' },
];

const ROLE_MAP = { HR: 'hr', Manager: 'business_manager', CEO: 'ceo' };

/* ─── Apple-style primitives ─── */

const StatusDot = ({ status }) => {
  const s = (status || '').toLowerCase();
  return <span className={`apple-status ${s}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>;
};

const fmtDate = (d) => {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtShort = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};
const fmtTime = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

/* ─── Approval stepper ─── */
const ApprovalStepper = ({ chain, approvals }) => {
  if (!chain || chain.length === 0) return null;
  const actedMap = {};
  (approvals || []).forEach(a => { actedMap[a.role] = a; });

  return (
    <div className="apple-stepper">
      {chain.map((step, i) => {
        const acted = actedMap[step.role] || (step.status === 'approved' ? step : null);
        const isApproved = (acted && (acted.action === 'approve' || acted.action === 'approved')) || step.status === 'approved';
        const isRejected = (acted && (acted.action === 'reject' || acted.action === 'rejected')) || step.status === 'rejected';
        const initials = (step.user_name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
        const comment = acted?.comment || step.comment || '';
        return (
          <React.Fragment key={i}>
            {i > 0 && <div className="apple-step-connector" />}
            <div className="apple-step">
              <div className={`apple-step-circle ${isApproved ? 'done' : isRejected ? 'rejected' : 'waiting'}`}>
                {isApproved ? <Check className="h-3 w-3" /> : isRejected ? <XIcon className="h-3 w-3" /> : initials}
              </div>
              <div className="flex flex-col">
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--apple-text)' }}>{step.user_name}</span>
                <span style={{ fontSize: 11, color: 'var(--apple-secondary)' }}>
                  {step.role}{acted?.ts ? ` · ${fmtShort(acted.ts)} ${fmtTime(acted.ts)}` : step.ts ? ` · ${fmtShort(step.ts)} ${fmtTime(step.ts)}` : ''}
                </span>
                {comment && (
                  <span style={{ fontSize: 11, color: '#6366f1', fontStyle: 'italic', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                    title={comment}>
                    "{comment}"
                  </span>
                )}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ─── Action logic ─── */
const canUserAct = (item, userRole) => {
  if (item.status !== 'pending') return false;
  const spRole = ROLE_MAP[userRole];
  
  // Check approval_chain for a pending step matching user's role (most reliable)
  const chain = item.approval_chain || [];
  if (chain.length > 0) {
    const myStep = chain.find(s => s.role === spRole && (s.status === 'pending'));
    if (myStep) return true;
    // Also check if all prior steps are done and this step is 'waiting' (next in line)
    const firstWaiting = chain.find(s => s.status === 'pending' || s.status === 'waiting');
    if (firstWaiting && firstWaiting.role === spRole) return true;
  }
  
  // Fallback: direct current_stage check
  if (item.current_stage) return item.current_stage === spRole;
  
  // Legacy fallback
  const actedRoles = new Set((item.approvals || []).map(a => a.role));
  if (chain.length === 0) return true;
  const nextStep = chain.find(s => !actedRoles.has(s.role));
  return nextStep?.role === spRole;
};
const userAlreadyActed = (item, userRole) => {
  const spRole = ROLE_MAP[userRole];
  return (item.approvals || []).some(a => a.role === spRole);
};

const ActionCell = ({ item, onAction, loading, userRole }) => {
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);

  if (item.status === 'approved' || item.status === 'rejected' || item.current_stage === 'completed') {
    return <StatusDot status={item.status} />;
  }
  if (userAlreadyActed(item, userRole)) {
    return <span style={{ fontSize: 12, color: 'var(--apple-secondary)', fontWeight: 500 }}>Acted</span>;
  }
  if (!canUserAct(item, userRole)) {
    const chain = item.approval_chain || [];
    const actedRoles = new Set((item.approvals || []).map(a => a.role));
    const nextStep = chain.find(s => !actedRoles.has(s.role));
    return <span style={{ fontSize: 12, color: 'var(--apple-secondary)', fontStyle: 'italic' }}>Waiting: {nextStep?.user_name || item.current_stage || '…'}</span>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2 items-center">
        <button onClick={(e) => { e.stopPropagation(); onAction(item.id, 'approve', comment || null); }} disabled={loading}
          style={{ background: 'var(--apple-blue)', color: 'white', fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}
          data-testid={`approve-btn-${item.id}`}>
          Approve
        </button>
        <button onClick={(e) => { e.stopPropagation(); onAction(item.id, 'reject', comment || null); }} disabled={loading}
          style={{ background: 'white', color: 'var(--apple-red)', fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 8, border: '1px solid var(--apple-red)', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}
          data-testid={`reject-btn-${item.id}`}>
          Reject
        </button>
        <button onClick={(e) => { e.stopPropagation(); setShowComment(!showComment); }}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--apple-secondary)', padding: 4 }}>
          <MessageSquare className="h-4 w-4" />
        </button>
      </div>
      {showComment && (
        <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Comment (optional)"
          onClick={e => e.stopPropagation()}
          style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--apple-border)', outline: 'none', width: '100%' }} />
      )}
    </div>
  );
};

/* ─── Table styles ─── */
const thStyle = { padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 500, color: 'var(--apple-secondary)', borderBottom: '1px solid var(--apple-divider)', whiteSpace: 'nowrap' };
const thCenter = { ...thStyle, textAlign: 'center' };
const tdStyle = { padding: '14px 16px', fontSize: 13, color: 'var(--apple-text)', borderBottom: '1px solid var(--apple-divider)' };
const tdCenter = { ...tdStyle, textAlign: 'center' };
const tdMono = { ...tdCenter, fontFamily: 'var(--font-mono)', fontFeatureSettings: '"tnum"' };
const trHover = { cursor: 'default' };

const TypePill = ({ text }) => (
  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#F0F0F0', color: '#1D1D1F' }}>
    {(text || '').toUpperCase()}
  </span>
);
const OutletPill = ({ text }) => (
  <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 6, background: '#F5F5F7', color: '#636366' }}>
    {text}
  </span>
);

/* ─── Table renderers ─── */

const LeavesTable = ({ items, onAction, loading, userRole }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
    <thead>
      <tr>
        <th style={thStyle}>Employee</th>
        <th style={thCenter}>Type</th>
        <th style={thCenter}>From</th>
        <th style={thCenter}>To</th>
        <th style={thCenter}>Days</th>
        <th style={thStyle}>Reason</th>
        <th style={thCenter}>Outlet</th>
        <th style={thStyle}>Approval chain</th>
        <th style={thCenter}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {items.map(item => (
        <tr key={item.id} style={trHover} className="hover:bg-[#FAFAFA]" data-testid={`row-${item.id}`}>
          <td style={{ ...tdStyle, fontWeight: 600 }}>{item.employee_name}</td>
          <td style={tdCenter}><TypePill text={item.leave_type} /></td>
          <td style={tdMono}>{fmtDate(item.from_date)}</td>
          <td style={tdMono}>{fmtDate(item.to_date)}</td>
          <td style={{ ...tdMono, fontWeight: 600 }}>{item.days}</td>
          <td style={{ ...tdStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#636366' }}>{item.reason || '–'}</td>
          <td style={tdCenter}><OutletPill text={item.outlet_name} /></td>
          <td style={tdStyle}><ApprovalStepper chain={item.approval_chain} approvals={item.approvals} /></td>
          <td style={tdCenter}><ActionCell item={item} onAction={onAction} loading={loading} userRole={userRole} /></td>
        </tr>
      ))}
    </tbody>
  </table>
);

const MissedPunchTable = ({ items, onAction, loading, userRole }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
    <thead>
      <tr>
        <th style={thStyle}>Employee</th>
        <th style={thCenter}>Code</th>
        <th style={thCenter}>Date</th>
        <th style={thCenter}>Type</th>
        <th style={thCenter}>Claimed time</th>
        <th style={thStyle}>Reason</th>
        <th style={thCenter}>Status</th>
        <th style={thCenter}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {items.map(item => (
        <tr key={item.id} className="hover:bg-[#FAFAFA]">
          <td style={{ ...tdStyle, fontWeight: 600 }}>{item.employee_name}</td>
          <td style={{ ...tdMono, color: '#636366' }}>{item.employee_code}</td>
          <td style={tdMono}>{fmtDate(item.date)}</td>
          <td style={tdCenter}><TypePill text={item.type} /></td>
          <td style={tdMono}>{item.claimed_in_time || item.claimed_out_time || '–'}</td>
          <td style={{ ...tdStyle, color: '#636366', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.reason || '–'}</td>
          <td style={tdCenter}><StatusDot status={item.status} /></td>
          <td style={tdCenter}><ActionCell item={item} onAction={onAction} loading={loading} userRole={userRole} /></td>
        </tr>
      ))}
    </tbody>
  </table>
);

const TransfersTable = ({ items, onAction, loading, userRole }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
    <thead>
      <tr>
        <th style={thStyle}>Employee</th>
        <th style={thCenter}>From</th>
        <th style={thCenter}>To</th>
        <th style={thCenter}>Effective</th>
        <th style={thStyle}>Reason</th>
        <th style={thStyle}>Approval chain</th>
        <th style={thCenter}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {items.map(item => (
        <tr key={item.id} className="hover:bg-[#FAFAFA]">
          <td style={{ ...tdStyle, fontWeight: 600 }}>{item.employee_name}</td>
          <td style={tdCenter}><OutletPill text={item.from_outlet_name} /></td>
          <td style={tdCenter}><OutletPill text={item.to_outlet_name} /></td>
          <td style={tdMono}>{fmtDate(item.effective_date)}</td>
          <td style={{ ...tdStyle, color: '#636366' }}>{item.reason || '–'}</td>
          <td style={tdStyle}><ApprovalStepper chain={item.approval_chain} approvals={item.approvals} /></td>
          <td style={tdCenter}><ActionCell item={item} onAction={onAction} loading={loading} userRole={userRole} /></td>
        </tr>
      ))}
    </tbody>
  </table>
);

/* ─── Roster table with expandable schedule ─── */
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const fmtShortDate = (d) => { if (!d) return ''; const dt = new Date(d + 'T00:00:00'); return `${dt.getDate()} ${dt.toLocaleString('en-IN', { month: 'short' })}`; };
const fmtDay = (d) => { if (!d) return ''; return DAY_NAMES[new Date(d + 'T00:00:00').getDay()]; };

const RosterSchedule = ({ entries, designationMap }) => {
  if (!entries || entries.length === 0) return <p style={{ fontSize: 12, color: 'var(--apple-secondary)', padding: 16 }}>No shift entries.</p>;
  const empMap = {};
  entries.forEach(e => { if (!empMap[e.employee_name]) empMap[e.employee_name] = {}; empMap[e.employee_name][e.date] = e; });
  const employees = Object.entries(empMap).sort(([a], [b]) => a.localeCompare(b));
  const dates = [...new Set(entries.map(e => e.date))].sort();
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--apple-divider)' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--apple-text)', position: 'sticky', left: 0, background: 'white', zIndex: 1, minWidth: 120 }}>Employee</th>
            {dates.map(d => (<th key={d} style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 500, color: 'var(--apple-secondary)', minWidth: 80 }}><div>{fmtDay(d)}</div><div style={{ fontSize: 10, fontWeight: 400 }}>{fmtShortDate(d)}</div></th>))}
          </tr>
        </thead>
        <tbody>
          {employees.map(([name, schedule]) => (
            <tr key={name} style={{ borderBottom: '1px solid var(--apple-divider)' }}>
              <td style={{ padding: '6px 12px', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'white', zIndex: 1 }}>
                <div style={{ fontWeight: 600, color: 'var(--apple-text)', fontSize: 11 }}>{name}</div>
                {designationMap && designationMap[name] && <div style={{ fontSize: 10, color: 'var(--apple-secondary)' }}>{designationMap[name]}</div>}
              </td>
              {dates.map(d => { const entry = schedule[d]; if (!entry) return <td key={d} style={{ textAlign: 'center', color: '#D1D1D6' }}>—</td>; const isOff = entry.shift_type === 'OFF' || entry.shift_type === 'WO'; return (<td key={d} style={{ textAlign: 'center', padding: '4px 2px' }}>{isOff ? <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: 'var(--apple-blue-10)', color: 'var(--apple-blue)' }}>OFF</span> : <div><div style={{ fontSize: 11, fontWeight: 600, color: 'var(--apple-text)' }}>{entry.shift_template_name || entry.shift_type}</div><div style={{ fontSize: 10, color: 'var(--apple-secondary)' }}>{entry.shift_time}</div></div>}</td>); })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RostersTable = ({ items, onAction, loading, userRole, outlets, spEmployees }) => {
  const [expandedId, setExpandedId] = React.useState(null);
  const outletMap = {};
  (outlets || []).forEach(o => { outletMap[o.id] = o.name; });
  const designationMap = {};
  (spEmployees || []).forEach(e => { if (e.full_name && e.designation) designationMap[e.full_name] = e.designation; });
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={thStyle}>Outlet / Created by</th>
          <th style={thCenter}>Month/Year</th>
          <th style={thCenter}>Entries</th>
          <th style={thCenter}>Status</th>
          <th style={thCenter}>Created</th>
          <th style={thStyle}>Approval chain</th>
          <th style={thCenter}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => {
          const entryCount = (item.entries || []).length;
          const empCount = new Set((item.entries || []).map(e => e.employee_name)).size;
          const isOpen = expandedId === item.id;
          return (
            <React.Fragment key={item.id}>
              <tr className="hover:bg-[#FAFAFA]" style={{ cursor: 'pointer' }} onClick={() => setExpandedId(isOpen ? null : item.id)}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{item.created_by_name || '–'}</td>
                <td style={tdCenter}><OutletPill text={outletMap[item.outlet_id] || '–'} /></td>
                <td style={tdMono}>{item.month}/{item.year}</td>
                <td style={tdCenter}><span style={{ fontSize: 11, color: '#636366' }}>{empCount} emp · {entryCount} shifts</span></td>
                <td style={tdCenter}><StatusDot status={item.status} /></td>
                <td style={tdMono}>{fmtDate(item.created_at)}</td>
                <td style={tdStyle}><ApprovalStepper chain={item.approval_chain} approvals={item.approvals} /></td>
                <td style={tdCenter} onClick={e => e.stopPropagation()}><ActionCell item={item} onAction={onAction} loading={loading} userRole={userRole} /></td>
              </tr>
              {isOpen && (
                <tr><td colSpan={7} style={{ padding: 0, background: '#FAFAFA', borderBottom: '2px solid var(--apple-border)' }}>
                  <div style={{ padding: '12px 16px' }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--apple-text)', marginBottom: 8 }}>Shift schedule — {empCount} employees, {entryCount} shifts</p>
                    <RosterSchedule entries={item.entries} designationMap={designationMap} />
                  </div>
                </td></tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const GenericTable = ({ items, onAction, loading, userRole, tab }) => {
  // Normalize OT/ODW items to standard field names based on active tab
  const isOdw = tab === 'odw';
  const normalize = (item) => {
    // ShiftPro stores both ot_status and odw_status on every attendance row (value 'none' when N/A),
    // so we must pick the field matching the active tab — not whichever is truthy.
    const effectiveStatus = isOdw ? (item.odw_status || item.status) : (item.ot_status || item.status);
    return {
      ...item,
      employee_name: item.employee_name || item.user_name || item.name || '–',
      status: effectiveStatus,
      approvals: (isOdw ? item.odw_approvals : item.ot_approvals) || item.approvals || [],
      approval_chain: item.approval_chain || [],
      current_stage: (isOdw ? item.odw_current_stage : item.ot_current_stage) || item.current_stage || '',
    };
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={thStyle}>Employee</th>
          <th style={thCenter}>Date</th>
          <th style={thStyle}>Details</th>
          <th style={thCenter}>Outlet</th>
          <th style={thCenter}>Status</th>
          <th style={thStyle}>Approval chain</th>
          <th style={thStyle}>Comments</th>
          <th style={thCenter}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map(raw => {
          const item = normalize(raw);
          const comments = item.approver_comments || [];
          return (
            <tr key={item.id || item._id} className="hover:bg-[#FAFAFA]">
              <td style={{ ...tdStyle, fontWeight: 600 }}>{item.employee_name}</td>
              <td style={tdMono}>{fmtDate(item.date || item.from_date || item.created_at)}</td>
              <td style={{ ...tdStyle, color: '#636366', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.reason || item.notes || (item.ot_hours ? `OT: ${item.ot_hours}h` : '–')}</td>
              <td style={tdCenter}><OutletPill text={item.outlet_name || item.outlet || '–'} /></td>
              <td style={tdCenter}><StatusDot status={item.status} /></td>
              <td style={tdStyle}><ApprovalStepper chain={item.approval_chain} approvals={item.approvals} /></td>
              <td style={{ ...tdStyle, maxWidth: 250, fontSize: 11 }}>
                {comments.length > 0 ? comments.map((c, i) => (
                  <div key={i} style={{ marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, color: '#4338CA', fontSize: 10 }}>{c.name}:</span>{' '}
                    <span style={{ color: '#374151' }}>{c.comment}</span>
                  </div>
                )) : <span style={{ color: '#D1D5DB' }}>–</span>}
              </td>
              <td style={tdCenter}><ActionCell item={item} onAction={onAction} loading={loading} userRole={userRole} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/* ─── Main component ─── */

const ShiftProApprovals = ({ user }) => {
  const [activeTab, setActiveTab] = useState('leaves');
  const [viewMode, setViewMode] = useState('pending');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [outlets, setOutlets] = useState([]);
  const [spEmployees, setSpEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [selectedType, setSelectedType] = useState('');

  const fetchCounts = useCallback(async () => {
    try { const res = await api.get(`/shiftpro/approvals/counts?mps_role=${user?.role || ''}`); setCounts(res.data?.counts || {}); } catch {}
  }, [user?.role]);

  const fetchOutlets = useCallback(async () => {
    try { const res = await api.get('/shiftpro/outlets'); setOutlets(res.data || []); } catch {}
  }, []);

  const fetchSpEmployees = useCallback(async () => {
    try { const res = await api.get('/shiftpro/employees'); setSpEmployees(Array.isArray(res.data) ? res.data : []); } catch {}
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const suffix = viewMode === 'pending' ? 'pending' : 'all';
      let url = `/shiftpro/approvals/${activeTab}/${suffix}?mps_role=${user?.role || ''}`;
      if (viewMode === 'all' && (activeTab === 'ot' || activeTab === 'odw')) {
        if (fromDate) url += `&from_date=${fromDate}`;
        if (toDate) url += `&to_date=${toDate}`;
      }
      const res = await api.get(url);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error(`Failed to fetch ${activeTab} data`); setItems([]); }
    finally { setLoading(false); }
  }, [activeTab, viewMode, user?.role, fromDate, toDate]);

  useEffect(() => { fetchCounts(); fetchOutlets(); fetchSpEmployees(); }, [fetchCounts, fetchOutlets, fetchSpEmployees]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleAction = async (id, action, comment) => {
    setActionLoading(true);
    try {
      let url;
      if (activeTab === 'ot') url = `/shiftpro/approvals/ot/${id}/action`;
      else if (activeTab === 'odw') url = `/shiftpro/approvals/odw/${id}/action`;
      else url = `/shiftpro/approvals/${activeTab}/${id}/action`;
      await api.post(url, { action, comment, mps_role: user?.role });
      toast.success(`Successfully ${action}d`);
      fetchItems(); fetchCounts();
    } catch (err) { toast.error(err?.response?.data?.detail || `Failed to ${action}`); }
    finally { setActionLoading(false); }
  };

  const getItemDate = (item) => item.from_date || item.date || item.effective_date || (item.created_at || '').slice(0, 10) || '';
  const getItemType = (item) => item.leave_type || item.type || item.shift_type || '';
  const getItemOutlet = (item) => item.outlet_name || item.outlet || item.from_outlet_name || '';
  const uniqueTypes = [...new Set(items.map(getItemType).filter(Boolean))].sort();

  const filtered = items.filter(item => {
    if (search) { const s = search.toLowerCase(); const name = (item.employee_name || item.user_name || item.name || '').toLowerCase(); const code = (item.employee_code || '').toLowerCase(); if (!name.includes(s) && !code.includes(s)) return false; }
    const itemDate = getItemDate(item);
    if (fromDate && itemDate && itemDate < fromDate) return false;
    if (toDate && itemDate && itemDate > toDate) return false;
    if (selectedOutlet && getItemOutlet(item) !== selectedOutlet) return false;
    if (selectedType && getItemType(item) !== selectedType) return false;
    return true;
  });

  const renderTable = () => {
    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: 'var(--apple-border)', borderTopColor: 'var(--apple-blue)' }} /></div>;
    if (filtered.length === 0) return <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--apple-secondary)', fontSize: 14 }}>No {viewMode} {TABS.find(t => t.key === activeTab)?.label} records.</div>;
    const props = { items: filtered, onAction: handleAction, loading: actionLoading, userRole: user?.role };
    switch (activeTab) {
      case 'leaves': return <LeavesTable {...props} />;
      case 'rosters': return <RostersTable {...props} outlets={outlets} spEmployees={spEmployees} />;
      case 'missed-punch': return <MissedPunchTable {...props} />;
      case 'transfers': return <TransfersTable {...props} />;
      default: return <GenericTable {...props} tab={activeTab} />;
    }
  };

  return (
    <div style={{ maxWidth: 1400 }} data-testid="shiftpro-approvals-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--apple-text)', letterSpacing: '-0.02em' }}>Approvals</h1>
          <p style={{ fontSize: 14, color: 'var(--apple-secondary)', marginTop: 2 }}>ShiftPro approval requests</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => { fetchItems(); fetchCounts(); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--apple-blue)', background: 'none', border: 'none', cursor: 'pointer' }}
            data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          {/* Export button for all tabs */}
          <button onClick={async () => {
              try {
                let exportUrl = `/shiftpro/approvals/export-excel?tab=${activeTab}&view=${viewMode}`;
                if (viewMode === 'all' && (activeTab === 'ot' || activeTab === 'odw')) {
                  if (fromDate) exportUrl += `&from_date=${fromDate}`;
                  if (toDate) exportUrl += `&to_date=${toDate}`;
                }
                const response = await api.get(exportUrl, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const a = document.createElement('a'); a.href = url; a.download = `shiftpro_${activeTab}_approvals.xlsx`; a.click();
              } catch (err) { console.error('Export failed', err); toast.error('Export failed'); }
            }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#059669', background: 'none', border: 'none', cursor: 'pointer' }}
              data-testid="export-approvals-btn">
              <Download className="h-4 w-4" /> Export Excel
            </button>
        </div>
      </div>

      {/* Card */}
      <div style={{ background: 'var(--apple-card)', border: '1px solid var(--apple-border)', borderRadius: 'var(--apple-radius-lg)', boxShadow: 'var(--apple-shadow)' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--apple-divider)', overflowX: 'auto', padding: '0 8px' }}>
          {TABS.map(tab => {
            const count = counts[tab.countKey] || 0;
            const isActive = activeTab === tab.key;
            return (
              <button key={tab.key}
                onClick={() => { setActiveTab(tab.key); setViewMode('pending'); }}
                style={{
                  padding: '14px 16px', fontSize: 13, fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'var(--apple-blue)' : 'var(--apple-secondary)',
                  borderBottom: isActive ? '2px solid var(--apple-blue)' : '2px solid transparent',
                  background: 'none', border: 'none', borderBottomStyle: 'solid',
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                  marginBottom: -1,
                }}
                data-testid={`tab-${tab.key}`}>
                {tab.label}
                {count > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: '#FF3B30', color: 'white' }}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--apple-divider)', flexWrap: 'wrap' }}>
          {/* Segmented control */}
          <div className="apple-segment">
            <button className={viewMode === 'pending' ? 'active' : ''} onClick={() => setViewMode('pending')} data-testid="filter-pending">Pending</button>
            <button className={viewMode === 'all' ? 'active' : ''} onClick={() => setViewMode('all')} data-testid="filter-all">All</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--apple-secondary)' }}>From</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--apple-border)', outline: 'none', color: 'var(--apple-text)' }} data-testid="filter-from-date" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--apple-secondary)' }}>To</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--apple-border)', outline: 'none', color: 'var(--apple-text)' }} data-testid="filter-to-date" />
          </div>
          <select value={selectedOutlet} onChange={e => setSelectedOutlet(e.target.value)}
            style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--apple-border)', color: 'var(--apple-text)', minWidth: 110 }} data-testid="filter-outlet">
            <option value="">All outlets</option>
            {outlets.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
          </select>
          <select value={selectedType} onChange={e => setSelectedType(e.target.value)}
            style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--apple-border)', color: 'var(--apple-text)', minWidth: 100 }} data-testid="filter-type">
            <option value="">All types</option>
            {uniqueTypes.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
          </select>
          <div style={{ position: 'relative', flex: 1, minWidth: 140, maxWidth: 240 }}>
            <Search className="h-4 w-4" style={{ position: 'absolute', left: 8, top: 7, color: 'var(--apple-secondary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employee…"
              style={{ width: '100%', fontSize: 12, padding: '5px 8px 5px 30px', borderRadius: 6, border: '1px solid var(--apple-border)', outline: 'none', color: 'var(--apple-text)' }} data-testid="approval-search" />
          </div>
          {(fromDate || toDate || selectedOutlet || selectedType || search) && (
            <button onClick={() => { setFromDate(''); setToDate(''); setSelectedOutlet(''); setSelectedType(''); setSearch(''); }}
              style={{ fontSize: 12, color: 'var(--apple-blue)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }} data-testid="clear-filters">Clear</button>
          )}
          <span style={{ fontSize: 12, color: 'var(--apple-secondary)', marginLeft: 'auto' }}>{filtered.length} records</span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }} data-testid="approvals-table-container">
          {renderTable()}
        </div>
      </div>
    </div>
  );
};

export default ShiftProApprovals;
